import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import { loadReCaptcha } from 'react-recaptcha-v3';

import omit from 'lodash/omit';
import gettext from '@eb/gettext';
import { isAnEmail } from '@eb/validators';

import { withMainControls } from '@eb/eds-structure';

import { AlertChunky } from '@eb/eds-iconography';
import { ICON_TYPE_PROP_TYPE } from '@eb/eds-icon';
import { TYPE_ERROR } from '@eb/eds-notification';
import { Button } from '@eb/eds-button';

import PageHeader from '../../components/PageHeader';
import SignupEmailForm from '../../components/newForms/SignupEmailForm';
import SignupForm from '../../components/newForms/SignupForm';
import SplitContainer from '../../connectedComponents/SplitContainer';
import ConnectedEventbritePrivacyTerms from '../../connectedComponents/PrivacyTerms';
import ConnectedSocialLoginOptions from '../../connectedComponents/ConnectedSocialLoginOptions';

import { setSocialMocks } from '../../utils/socialMocks';
import { SIGN_UP_FORM } from '../../constants';

/**
 * List of urls that will lead to this page:
 * * BASE_URL/SIGN_UP_PATH
 */
class SignupPage extends React.PureComponent {
    static propTypes = {
        onSignUpUser: PropTypes.func,
        email: PropTypes.string,
        locale: PropTypes.string,
        onLoginInsteadClick: PropTypes.func,
        trackEmailConfirmationEdit: PropTypes.func,
        trackEmailMismatchShowed: PropTypes.func,
        clearAuthNotification: PropTypes.func,
        recaptcha2PublicKey: PropTypes.string,
        recaptcha3PublicKey: PropTypes.string,
        disableRecaptcha3: PropTypes.bool,
        showCaptcha: PropTypes.bool,
        fp: PropTypes.bool,
        socialSSOSettings: PropTypes.shape({
            facebook: PropTypes.object,
        }),
        authNotificationOptions: PropTypes.shape({
            message: PropTypes.node,
            type: PropTypes.string,
            iconType: ICON_TYPE_PROP_TYPE,
        }),
        usesNewStyles: PropTypes.bool,
    };

    componentDidMount() {
        if (!this.props.disableRecaptcha3) {
            loadReCaptcha(this.props.recaptcha3PublicKey);
        }
    }

    _handleSubmit = (formData) => {
        const { clearAuthNotification, onSignupLookup } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        return onSignupLookup({ ...formData, isSignup: true });
    };

    _handleSubmitSignUp = (formData) => {
        const { clearAuthNotification, onSignUpUser } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        return onSignUpUser(formData);
    };

    _handleShowNotification = (error) => {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });

            this._hasNotification = true;
        }
    };

    _handleOnEmailEdit = () => {
        const { setSignupSecondStep, clearAuthNotification } = this.props;
        if (clearAuthNotification) {
            clearAuthNotification();
        }
        if (setSignupSecondStep) {
            setSignupSecondStep(false);
        }
    };

    _handleClearNotification = () => {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;
        hideMainNotification && hideMainNotification();
    };

    /**
     * Social login functions probably can be moved to actions as it is not doing anything with the component
     */
    _handleOnSocialLogIn = (socialProvider, socialResponse) => {
        const {
            clearAuthNotification,
            onSocialLogInUser,
            socialSSOSettings,
        } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        // add param to call api, depending on feature flag
        let skipCreation = false;

        if (socialSSOSettings) {
            skipCreation = socialSSOSettings.twoStepsSignup || false;
        }

        if (onSocialLogInUser) {
            onSocialLogInUser(socialProvider, {
                skipCreation,
                ...socialResponse,
            });
        }
    };

    _handleOnSocialProviderClick = async (
        socialProvider,
        stateFromProvider,
    ) => {
        const {
            onSocialProviderClick,
            // Social mocking props
            onSkipSocialLinks,
            skipSocialCalls,
        } = this.props;

        await setSocialMocks({
            socialProvider,
            onSkipSocialLinks,
            skipSocialCalls,
        });

        if (onSocialProviderClick) {
            onSocialProviderClick({
                socialProvider,
                stateFromProvider,
            });
        }
    };
    /**
     * Social login functions
     */

    render() {
        const {
            form,
            email,
            locale,
            recaptcha2PublicKey,
            showCaptcha,
            fp,
            socialSSOSettings,
            authNotificationOptions,
            clearAuthNotification,
            usesNewStyles,
            signUpSecondStep,
            trackEmailConfirmationEdit,
            trackEmailMismatchShowed,
            previousFormEmail,
            onLoginInsteadClick,
        } = this.props;

        let syncErrors;

        if (form[SIGN_UP_FORM]) {
            syncErrors = form[SIGN_UP_FORM].syncErrors;
        }

        let pageHeaderProps = {
            headerText: gettext('Create an account'),
            subtitleText: null,
            usesNewStyles,
        };
        const buttonCTA = gettext('Continue');
        const buttonCTACreate = gettext('Create account');

        const formProps = {
            formErrors: syncErrors,
            onLoginInsteadClick: this._trackLoginInsteadClick,
            clearAuthNotification: clearAuthNotification,
            locale: locale,
            recaptcha2PublicKey: recaptcha2PublicKey,
            showCaptcha: showCaptcha,
            email: email,
            fp: fp,
            showNotification: this._handleShowNotification,
            clearNotification: this._handleClearNotification,
            socialSSOSettings: socialSSOSettings,
        };
        let signupScreen = (
            <div>
                <SignupEmailForm
                    {...formProps}
                    buttonCTA={buttonCTA}
                    onSubmit={this._handleSubmit}
                    initialValues={{ email: previousFormEmail }}
                />
                <div className="split-container__divider eds-l-pad-bot-5 eds-l-pad-top-5">
                    <div className="split-container__divider-text eds-text-bm eds-text-weight--heavy">
                        {gettext('or')}
                    </div>
                </div>
                <div data-testid="social-login-section">
                    <ConnectedSocialLoginOptions
                        onSocialConnectVerified={this._handleOnSocialLogIn}
                        handleSocialProviderClick={
                            this._handleOnSocialProviderClick
                        }
                        buttonAppleText={gettext('Sign up with Apple')}
                        buttonFBText={gettext('Sign up with Facebook')}
                        buttonGoogleText={gettext('Sign up with Google')}
                    />
                </div>
            </div>
        );

        if (signUpSecondStep && email && isAnEmail(email)) {
            // Then show the second screen
            signupScreen = (
                <SignupForm
                    {...formProps}
                    buttonCTA={buttonCTACreate}
                    onSubmit={this._handleSubmitSignUp}
                    trackEmailConfirmationEdit={trackEmailConfirmationEdit}
                    trackEmailMismatchShowed={trackEmailMismatchShowed}
                    handleEmailEdit={this._handleOnEmailEdit}
                />
            );
        }

        // hide subtitle when an auth notification is being displayed
        if (!isEmpty(authNotificationOptions)) {
            pageHeaderProps = omit(pageHeaderProps, 'subtitleText');
        }

        return (
            <SplitContainer
                shouldShowSecondaryPanel={usesNewStyles}
                usesNewStyles={usesNewStyles}
                header={<PageHeader {...pageHeaderProps} />}
            >
                {signupScreen}
                <div className="eds-text-color--ui-800 eds-text-weight--heavy eds-l-pad-bot-5 eds-l-pad-top-5">
                    <Button
                        onClick={onLoginInsteadClick}
                        type="button"
                        style="anchor"
                        data-testid="login-instead"
                    >
                        {gettext('Log In')}
                    </Button>
                </div>
                <ConnectedEventbritePrivacyTerms />
            </SplitContainer>
        );
    }
}

export default withMainControls(SignupPage);
